// Shared "possible duplicate sale" matching logic.
//
// Two sales are considered possible duplicates when ANY of these hold:
//   - Name : normalized strings equal, OR within a small Levenshtein edit
//            distance (exact-only when the shorter normalized length < 8,
//            else distance <= 2).
//   - Phone: equal after stripping non-digits (empty ignored).
//   - IC   : equal after stripping non-digits (empty ignored).
//
// Pure functions only — no imports — so this can be reused on the create forms,
// the list views, and (mirrored) on the C# backend.

// Lowercase + strip everything except a-z0-9 (removes spaces/punctuation).
export function normName(s) {
    return String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Strip all non-digit characters.
export function normDigits(s) {
    return String(s ?? '').replace(/\D/g, '');
}

// Classic Levenshtein edit distance (insert / delete / substitute).
export function levenshtein(a, b) {
    a = String(a ?? '');
    b = String(b ?? '');
    if (a === b) return 0;
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;

    let prev = new Array(b.length + 1);
    let curr = new Array(b.length + 1);
    for (let j = 0; j <= b.length; j++) prev[j] = j;

    for (let i = 1; i <= a.length; i++) {
        curr[0] = i;
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            curr[j] = Math.min(
                prev[j] + 1,        // deletion
                curr[j - 1] + 1,    // insertion
                prev[j - 1] + cost, // substitution
            );
        }
        [prev, curr] = [curr, prev];
    }
    return prev[b.length];
}

// Name match: exact normalized equality, OR small edit distance.
// Exact-only when the shorter normalized name is < 8 chars (too short to
// safely allow fuzziness); otherwise allow distance <= 2.
export function nameSimilar(a, b) {
    const na = normName(a);
    const nb = normName(b);
    if (!na || !nb) return false;
    if (na === nb) return true;
    const shorter = Math.min(na.length, nb.length);
    if (shorter < 8) return false;
    return levenshtein(na, nb) <= 2;
}

// salesMatch — each arg is an object { name, phone, ic }.
// Match if name is similar, OR phone equal (non-empty), OR ic equal (non-empty).
export function salesMatch(a, b) {
    if (!a || !b) return false;
    if (nameSimilar(a.name, b.name)) return true;

    const pa = normDigits(a.phone);
    const pb = normDigits(b.phone);
    if (pa && pb && pa === pb) return true;

    const ia = normDigits(a.ic);
    const ib = normDigits(b.ic);
    if (ia && ib && ia === ib) return true;

    return false;
}

// findDuplicateIdSet — given a list of items and a getKeyFields(item) that
// returns { id, name, phone, ic }, return a Set of ids that have >= 1 OTHER
// item matching them. O(n^2) over the loaded rows (fine for typical list sizes).
export function findDuplicateIdSet(items, getKeyFields) {
    const result = new Set();
    if (!Array.isArray(items) || items.length < 2) return result;

    const keys = items.map(getKeyFields);
    for (let i = 0; i < keys.length; i++) {
        if (!keys[i] || keys[i].id == null) continue;
        for (let j = i + 1; j < keys.length; j++) {
            if (!keys[j] || keys[j].id == null) continue;
            if (salesMatch(keys[i], keys[j])) {
                result.add(keys[i].id);
                result.add(keys[j].id);
            }
        }
    }
    return result;
}
